import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Clock, CheckCircle, XCircle, AlertTriangle,
  Eye, Download, Filter, Search
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { transferAPI } from '../api/api';

const TransactionsPage = () => {
  const navigate = useNavigate();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTransactions();
  }, []);

  const loadTransactions = async () => {
    try {
      const response = await transferAPI.getTransactions();
      setTransactions(response.data);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="w-5 h-5 text-terminal-green" />;
      case 'FAILED':
      case 'CANCELLED':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'PENDING':
      case 'PROCESSING':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Clock className="w-5 h-5 text-terminal-green-dim" />;
    }
  };

  const filteredTransactions = transactions.filter(txn => {
    const matchesFilter = filter === 'all' || txn.status.toLowerCase() === filter.toLowerCase();
    const matchesSearch = txn.transaction_reference.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         txn.recipient_info?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        >
          <Clock className="w-12 h-12 text-terminal-green" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-terminal-green p-4 md:p-8">
      <div className="scanline" />
      
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-terminal-green-dim hover:text-terminal-green mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>BACK TO DASHBOARD</span>
          </button>
          
          <h1 className="text-3xl md:text-4xl font-bold cyber-font glow-text mb-2">
            TRANSACTION HISTORY
          </h1>
          <p className="text-terminal-green-dim text-sm">
            View and track all your transfers
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-6 terminal-card p-4"
        >
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-terminal-green-dim" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="SEARCH BY REFERENCE OR RECIPIENT..."
                className="w-full pl-10 pr-4 py-3 terminal-input"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'completed', 'pending', 'failed'].map(status => (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  className={`px-4 py-2 text-sm border transition-all ${
                    filter === status
                      ? 'border-terminal-green bg-terminal-green/20'
                      : 'border-terminal-green/50 hover:border-terminal-green'
                  }`}
                >
                  {status.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        <div className="space-y-4">
          {filteredTransactions.length > 0 ? (
            filteredTransactions.map((txn, index) => (
              <motion.div
                key={txn.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="terminal-card p-6 cursor-pointer"
                onClick={() => navigate(`/transaction/${txn.id}`)}
                data-testid={`transaction-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(txn.status)}
                    <div>
                      <div className="text-xs text-terminal-green-dim">REFERENCE</div>
                      <div className="font-mono text-sm">{txn.transaction_reference}</div>
                    </div>
                  </div>

                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-xs text-terminal-green-dim">TYPE</div>
                      <div className="text-sm">{txn.transfer_type.replace('S2S/', '')}</div>
                    </div>
                    <div>
                      <div className="text-xs text-terminal-green-dim">AMOUNT</div>
                      <div className="text-sm font-bold">
                        ${txn.amount.toLocaleString()} {txn.currency}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-terminal-green-dim">RECIPIENT</div>
                      <div className="text-sm truncate">{txn.recipient_info}</div>
                    </div>
                    <div>
                      <div className="text-xs text-terminal-green-dim">DATE</div>
                      <div className="text-sm">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-3 py-1 border ${
                      txn.status === 'COMPLETED' ? 'border-terminal-green text-terminal-green' :
                      txn.status === 'FAILED' ? 'border-red-500 text-red-500' :
                      'border-yellow-500 text-yellow-500'
                    }`}>
                      {txn.status}
                    </span>
                    <Eye className="w-5 h-5 text-terminal-green-dim" />
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="terminal-card p-12 text-center"
            >
              <Clock className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-terminal-green-dim">No transactions found</p>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TransactionsPage;
